import { makeReference, useApolloClient, useSubscription } from '@apollo/client';
import { Grid } from '@mui/material';
import { invalidateApolloClientCache } from 'hooks/apollo/common';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { emptyString } from 'constants/defaultValues';
import { INSURANCE_OFFER_CREATION_FAILED, INSURANCE_RENEWAL_OFFER_CREATED } from '../../../subscriptions';
import { DOCUMENT_STATE_UPDATED } from 'features/insuranceDocument/stepper/subscriptions';
import { usePolicyRenewalUtils } from '../../utils';
import PolicyRenewalItemGeneralDataComponent from './PolicyRenewalItemGeneralDataComponent';
import PolicyRenewalItemSelectionComponent from './PolicyRenewalItemSelectionComponent';
import PolicyRenewalItemQuotationsContainer from './quotations/PolicyRenewalItemQuotationsContainer';
import { Card } from '@totalsoft/rocket-ui';

const PolicyRenewalItemContainer = ({ item }) => {
  const client = useApolloClient();
  const { setAuxiliaryDataCache } = usePolicyRenewalUtils();

  const [renewalItem, setRenewalItem] = useState(item);

  // #region Handlers

  const updateRenewalDocument = useCallback(overridenProps => {
    setRenewalItem(prev => ({
      ...prev,
      renewalPolicyDocument: {
        ...prev.renewalPolicyDocument,
        ...overridenProps
      }
    }));
  }, []);

  const updateAuxiliaryInfo = useCallback(
    message => {
      /* If message is empty, just remove it from cache */
      if (!message)
        invalidateApolloClientCache(client, makeReference('ROOT_QUERY'), [
          `renewalDocumentAuxiliaryData({"renewalDocumentId":${renewalItem.documentId}})`
        ]);
      else setAuxiliaryDataCache(renewalItem.documentId, message);

      updateRenewalDocument({
        additionalInfo: message
      });
    },
    [client, renewalItem, setAuxiliaryDataCache, updateRenewalDocument]
  );

  // #endregion

  // #region Subscriptions

  useSubscription(INSURANCE_RENEWAL_OFFER_CREATED, {
    variables: { renewalDocumentId: renewalItem?.documentId },
    skip: !renewalItem?.documentId,
    onData: ({
      data: {
        data: { insuranceRenewalOfferCreated }
      }
    }) => {
      updateRenewalDocument({
        documentId: insuranceRenewalOfferCreated.documentId,
        documentNumber: insuranceRenewalOfferCreated.documentNumber,
        documentState: insuranceRenewalOfferCreated.documentStateName
      });
      updateAuxiliaryInfo(emptyString);
    }
  });

  useSubscription(INSURANCE_OFFER_CREATION_FAILED, {
    variables: { renewalDocumentId: renewalItem?.documentId },
    skip: !renewalItem?.documentId,
    onData: ({
      data: {
        data: { insuranceOfferCreationFailed }
      }
    }) => {
      updateAuxiliaryInfo(insuranceOfferCreationFailed.errorMessage);
    }
  });

  useSubscription(DOCUMENT_STATE_UPDATED, {
    variables: { documentId: renewalItem?.renewalPolicyDocument?.documentId },
    skip: !renewalItem?.renewalPolicyDocument?.documentId,
    onData: ({
      data: {
        data: { documentStateUpdated }
      }
    }) => {
      updateRenewalDocument({
        documentState: documentStateUpdated.documentStateName
      });
    }
  });

  // #endregion

  return (
    <Card>
      <Grid container spacing={1} sx={{ position: 'relative' }}>
        <PolicyRenewalItemSelectionComponent item={renewalItem} />
        <Grid item container xs={12}>
          <PolicyRenewalItemGeneralDataComponent item={renewalItem} />
          <PolicyRenewalItemQuotationsContainer item={renewalItem} updateAuxiliaryInfo={updateAuxiliaryInfo} />
        </Grid>
      </Grid>
    </Card>
  );
};

PolicyRenewalItemContainer.propTypes = {
  item: PropTypes.object.isRequired
};

export default PolicyRenewalItemContainer;
